import React, { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { ShipmentPlan, updateShipmentPlanMeta } from '@/integrations/supabase/shipmentPlanService';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plan: ShipmentPlan | null;
}

export function EditShipmentPlanDialog({ open, onOpenChange, plan }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [reportName, setReportName] = useState('');
  const [reportDate, setReportDate] = useState('');
  const [summary, setSummary] = useState('');

  useEffect(() => {
    if (plan) {
      setReportName(plan.report_name || '');
      setReportDate((plan.report_date || '').slice(0, 10));
      setSummary(plan.summary || '');
    }
  }, [plan]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!plan) return;
      await updateShipmentPlanMeta(plan.id, {
        report_name: reportName.trim(),
        report_date: reportDate,
        summary: summary.trim() || null,
      });
    },
    onSuccess: async () => {
      toast({ title: 'Cập nhật thành công' });
      await queryClient.refetchQueries({ queryKey: ['shipment-plans'] });
      onOpenChange(false);
    },
    onError: (err: Error) => {
      toast({ title: 'Cập nhật thất bại', description: err.message, variant: 'destructive' });
    },
  });

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onOpenChange(false)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Chỉnh sửa Shipment Plan</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }}
          className="space-y-4"
        >
          <div className="space-y-1">
            <Label htmlFor="edit_report_name">Tên báo cáo</Label>
            <Input
              id="edit_report_name"
              value={reportName}
              onChange={(e) => setReportName(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="edit_report_date">Ngày báo cáo</Label>
            <Input
              id="edit_report_date"
              type="date"
              value={reportDate}
              onChange={(e) => setReportDate(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="edit_summary">Mô tả</Label>
            <Textarea
              id="edit_summary"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Hủy</Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Đang lưu...' : 'Lưu'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
